import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Account, Organization, Group } from '@/types/icrop';
import {
  WorkspaceContext,
  type WorkspaceContextValue,
  type WorkspaceState,
  type WorkspaceProfile,
} from '@/contexts/WorkspaceContext';

const SUPER_ADMIN_AUDIT_SESSION_PREFIX = 'icrop:super_admin_audit:';

async function logSuperAdminWorkspaceAccess(accountId: string, slug: string): Promise<void> {
  // Idempotency guard: one entry per (session, workspace) pair. The RPC
  // itself verifies the caller is super_admin so a missing/cleared
  // sessionStorage just means a duplicate log entry at worst.
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const key = `${SUPER_ADMIN_AUDIT_SESSION_PREFIX}${accountId}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, String(Date.now()));
    }
    const { error } = await supabase.rpc('log_super_admin_workspace_access', {
      target_account_id: accountId,
      target_slug: slug,
    });
    if (error) {
      console.warn('super_admin workspace access log failed:', error);
    }
  } catch (err) {
    console.warn('super_admin workspace access log threw:', err);
  }
}

interface WorkspaceProviderProps {
  /** Workspace slug from the URL host, or `null` for marketing/admin shells. */
  slug?: string | null;
  children: React.ReactNode;
}

export function WorkspaceProvider({ slug = null, children }: WorkspaceProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // Legacy super_admin in-memory account switcher (iCropDashboard depends on it).
  // For workspace subdomains, account is derived from the URL — this override is
  // only used inside the admin shell where slug === null.
  const [override, setOverride] = useState<Account | null>(null);

  const [state, setState] = useState<WorkspaceState>(slug ? { kind: 'loading' } : { kind: 'apex' });

  // Derive stable primitives from `user` so the resolution effect keys on the
  // user's IDENTITY, not the object reference. AuthProvider hands a NEW `user`
  // object on every onAuthStateChange event — including the hourly
  // TOKEN_REFRESHED — and depending on that object would reset the workspace to
  // `loading` and remount the whole router (destroying unsaved form/modal
  // state) on every silent refresh. `id` and the JWT `account_id` are what the
  // resolution below actually reads, and both are stable across refreshes.
  const userId = user?.id ?? null;
  const jwtAccountId =
    ((user?.app_metadata as Record<string, unknown> | undefined)?.account_id as
      | string
      | undefined) ?? null;

  // Resolve slug → account → state machine
  useEffect(() => {
    if (!slug) {
      setState({ kind: 'apex' });
      return;
    }
    if (authLoading) return;

    let cancelled = false;
    setState({ kind: 'loading' });

    (async () => {
      try {
        // Resolve the slug via the `resolve_workspace` SECURITY DEFINER RPC
        // (migration 036) rather than a direct `from('accounts').select()`.
        // The `accounts` RLS policy only exposes rows to JWT-scoped members /
        // super_admins, so a direct select returns nothing for an anonymous
        // pre-auth visitor — the SPA would then render "Workspace not found"
        // for every real workspace. The RPC bypasses RLS and returns the
        // public-safe subset (contact/billing redacted for non-members).
        const { data: account, error } = await supabase.rpc('resolve_workspace', {
          p_slug: slug,
        });

        if (cancelled) return;

        if (error || !account) {
          setState({ kind: 'not-found', slug });
          return;
        }

        // Soft-delete / cancellation are terminal — no override
        if (account.deleted_at) {
          setState({ kind: 'unavailable', account: account as Account, reason: 'deleted' });
          return;
        }
        if (account.subscription_status === 'cancelled') {
          setState({ kind: 'unavailable', account: account as Account, reason: 'cancelled' });
          return;
        }
        if (account.subscription_status === 'suspended') {
          setState({
            kind: 'suspended',
            account: account as Account,
            reason: (account as Account).suspended_reason ?? null,
          });
          return;
        }
        if (
          account.subscription_status === 'trialing' &&
          account.trial_ends_at &&
          new Date(account.trial_ends_at) < new Date()
        ) {
          setState({ kind: 'expired', account: account as Account });
          return;
        }

        // No user yet → render login with workspace branding.
        if (!userId) {
          setState({ kind: 'active', account: account as Account, profile: null });
          return;
        }

        // Logged-in users: super_admin bypasses the membership check.
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (cancelled) return;

        // Distinguish a genuine "no profile row" (data null, error null) from a
        // transient fetch FAILURE (timeout / network / PostgREST hiccup). On a
        // failure we must NOT fall through to the membership check below: a
        // super_admin would evaluate as non-super_admin and get stranded on the
        // terminal `denied` page. Surface a recoverable error instead — mirrors
        // ProtectedRoute's profileError → Retry handling.
        if (profileError) {
          console.error('WorkspaceContext profile fetch failed:', profileError);
          setState({ kind: 'error', slug });
          return;
        }

        const isSuperAdmin = profile?.role === 'super_admin';

        // Phase 2: super_admin always bypasses the JWT membership check —
        // they have a global role in `profiles.role` and Phase 1.5 migration
        // 026 grants the corresponding DB-level RLS bypass via
        // `public.is_super_admin()`. Without this explicit short-circuit, a
        // super_admin landing on a workspace whose `account_id` differs from
        // their JWT would be rejected here even though the database allows
        // the SELECT.
        if (isSuperAdmin) {
          setState({
            kind: 'active',
            account: account as Account,
            profile: (profile as WorkspaceProfile | null) ?? null,
          });

          // Cross-account audit log: fire the SECURITY DEFINER RPC once per
          // (session, workspace) pair. The session-storage key keeps tab
          // refreshes from spamming the log; the RPC double-checks
          // `profiles.role` server-side, so a tampered client can't forge
          // entries.
          if (jwtAccountId && jwtAccountId !== account.id) {
            void logSuperAdminWorkspaceAccess(account.id, account.slug);
          }
          return;
        }

        // Non-super_admin: the JWT's account_id is authoritative. If it
        // doesn't match the workspace slug's account, deny — the workspace
        // is for a different tenant.
        if (jwtAccountId !== account.id) {
          setState({ kind: 'denied', account: account as Account });
          return;
        }

        setState({
          kind: 'active',
          account: account as Account,
          profile: (profile as WorkspaceProfile | null) ?? null,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('WorkspaceContext slug resolution failed:', err);
          // A THROW here is transient/unexpected (fetch reject, PostgREST
          // hiccup) — a genuinely missing workspace is already handled above via
          // the returned `{ error } || !account` → `not-found` branch. Mapping
          // throws to the terminal `not-found` page would strand a super_admin
          // on a dead end with no Retry (the same #5 bug, on the throw path).
          // Route to the recoverable `error` kind so both paths converge.
          setState({ kind: 'error', slug });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, userId, jwtAccountId, authLoading]);

  // Legacy auto-load of the user's account when on the admin shell (slug === null
  // and user is logged in). Preserves the pre-Phase-1 behavior: profile.account
  // becomes the in-memory `account`.
  useEffect(() => {
    if (slug) return;
    if (!userId) {
      setOverride(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*, account:accounts(*)')
        .eq('id', userId)
        .single();
      if (!cancelled && profile && (profile as { account?: Account }).account) {
        setOverride((profile as { account: Account }).account);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Key on the stable `userId`, not the whole `user` object: AuthProvider hands
    // a NEW `user` on every onAuthStateChange (including the hourly
    // TOKEN_REFRESHED), which would otherwise re-fetch the profile and emit a
    // fresh account object on each silent refresh. The body only reads `user.id`.
  }, [slug, userId]);

  // Canonical account: state-derived if we're on a workspace; legacy override
  // otherwise (admin shell only).
  const stateAccount = state.kind === 'active' ? state.account : null;
  const account = stateAccount ?? override;

  // Allow callers (e.g. BrandingForm) to push a freshly-updated account back
  // into context. In workspace mode the source of truth is `state.account`;
  // in admin-shell mode it's the legacy `override`. A single setter keeps
  // the API symmetric for both.
  const setAccount = (next: Account | null) => {
    setState((prev) => {
      if (prev.kind === 'active' && next) {
        return { ...prev, account: next };
      }
      return prev;
    });
    setOverride(next);
  };

  // Orgs + groups (only when an account is bound)
  const {
    data: accountData,
    isLoading,
    error,
    refetch: refreshAccount,
  } = useQuery({
    queryKey: ['account-children', account?.id],
    queryFn: async () => {
      if (!account?.id) return null;

      // CML-137: membership is read from the `organization_groups` join, not the
      // single `organizations.group_id` scalar (which can only hold one group).
      // Fetch orgs, groups, and the join in parallel, then attach a full
      // `groups: Group[]` to each org. The FK embed on `groups` is dropped.
      const [organizationsResult, groupsResult, membershipsResult] = await Promise.all([
        supabase
          .from('organizations')
          .select('*')
          .eq('account_id', account.id)
          .eq('status', 'active')
          .order('name'),
        supabase
          .from('groups')
          .select('*')
          .eq('account_id', account.id)
          .eq('status', 'active')
          .order('name'),
        supabase
          .from('organization_groups')
          .select('organization_id, group_id')
          .eq('account_id', account.id),
      ]);

      if (organizationsResult.error) throw organizationsResult.error;
      if (groupsResult.error) throw groupsResult.error;
      if (membershipsResult.error) throw membershipsResult.error;

      const groups = (groupsResult.data ?? []) as Group[];
      const groupById = new Map(groups.map((g) => [g.id, g]));

      // organization_id → its member Group[] (active groups only; a membership
      // pointing at an inactive/hidden group resolves to nothing and is skipped,
      // matching the active-only groups list).
      const groupsByOrg = new Map<string, Group[]>();
      for (const m of (membershipsResult.data ?? []) as {
        organization_id: string;
        group_id: string;
      }[]) {
        const group = groupById.get(m.group_id);
        if (!group) continue;
        const list = groupsByOrg.get(m.organization_id) ?? [];
        list.push(group);
        groupsByOrg.set(m.organization_id, list);
      }

      const organizations = ((organizationsResult.data ?? []) as Organization[]).map(
        (org) => ({ ...org, groups: groupsByOrg.get(org.id) ?? [] }),
      );

      return { organizations, groups };
    },
    enabled: !!account?.id,
    staleTime: 5 * 60 * 1000,
  });

  const switchAccount = async (accountId: string): Promise<Account> => {
    const { data: newAccount, error: switchError } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', accountId)
      .single();
    if (switchError) throw switchError;
    setOverride(newAccount as Account);
    queryClient.invalidateQueries({ queryKey: ['account-children'] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    queryClient.invalidateQueries({ queryKey: ['groups'] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
    return newAccount as Account;
  };

  const getOrganizationsByGroup = (groupId: string): Organization[] =>
    (accountData?.organizations ?? []).filter((org) =>
      (org.groups ?? []).some((g) => g.id === groupId),
    );

  const getStandaloneOrganizations = (): Organization[] =>
    (accountData?.organizations ?? []).filter((org) => (org.groups ?? []).length === 0);

  const isGroupsEnabled = (): boolean =>
    account?.service_type === 'fsvp' &&
    account?.settings?.features?.groups_enabled !== false;

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      state,
      slug: slug ?? null,
      account,
      setAccount,
      organizations: accountData?.organizations ?? [],
      groups: accountData?.groups ?? [],
      isLoading,
      error: (error as Error | null) ?? null,
      refreshAccount,
      switchAccount,
      getOrganizationsByGroup,
      getStandaloneOrganizations,
      isGroupsEnabled,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, slug, account, accountData, isLoading, error],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/** @deprecated — use `WorkspaceProvider`. Kept for back-compat. */
export const AccountProvider = WorkspaceProvider;
